import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { lazy, Suspense, useState } from 'react';
import { IconExternal, IconMute, IconPhone, IconSos, IconSound } from '../components/icons';
import { Chips, Empty, ErrorNote, FlashNote, Loading, Note, PageHead, Pager, ReasonDialog, SosStatusPill, useFlash } from '../components/ui';
import { api, errorMessage } from '../lib/api';
import { ago, dateTime, phone, RIDE_STATUS_LABEL, time, VEHICLE_LABEL } from '../lib/format';
import { useAuth } from '../lib/auth';
import { markSosTaken, useStaffNames } from '../lib/queries';
import { qk } from '../lib/realtime-cache';
import { playSosChime, setSosSound, useSosSound } from '../lib/sosSound';
import type { Page, SosRecord, SosStatus } from '../lib/types';
import { sosRoute } from '../router';

const RouteMap = lazy(() => import('../components/RouteMap'));

function SosCard({ s, onResolve, onFlash }: { s: SosRecord; onResolve: (s: SosRecord) => void; onFlash: (t: string, tone?: 'good' | 'bad') => void }) {
  const qc = useQueryClient();
  const staffName = useStaffNames();
  const { admin } = useAuth();
  const ack = useMutation({
    mutationFn: () => api.post<SosRecord>(`/admin/sos/${s.id}/acknowledge`),
    onMutate: () => markSosTaken(qc, s.id, admin?.id),
    onSuccess: () => {
      onFlash('Marked as yours. Call them now.');
      void qc.invalidateQueries({ queryKey: qk.sos });
      void qc.invalidateQueries({ queryKey: qk.dashboard });
    },
    onError: (e) => {
      onFlash(errorMessage(e), 'bad');
      void qc.invalidateQueries({ queryKey: qk.sos });
    },
  });
  const snap = s.rideSnapshot;
  const raiser = s.raisedByType === 'RIDER' ? snap?.rider : snap?.driver;
  const other = s.raisedByType === 'RIDER' ? snap?.driver : snap?.rider;
  const cls = s.status === 'OPEN' ? 'open' : s.status === 'ACKNOWLEDGED' ? 'acked' : 'done';
  const where = s.lat != null && s.lng != null ? { lat: s.lat, lng: s.lng } : null;
  return (
    <section className={`card sos-card ${cls}`} data-testid={`sos-${s.id}`}>
      <div className="sos-band">
        <span className="beacon" aria-hidden="true" />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="strong">
            {s.raisedByType === 'RIDER' ? 'Rider' : 'Driver'} SOS · {ago(s.createdAt)}
          </div>
          <div className="small" style={{ opacity: 0.85 }}>
            {dateTime(s.createdAt)}
            {snap?.status ? ` · ride was ${RIDE_STATUS_LABEL[snap.status].toLowerCase()}` : ''}
          </div>
        </div>
        {s.status === 'OPEN' ? (
          <button className="btn btn-on-red" disabled={ack.isPending} onClick={() => ack.mutate()}>
            I’m on it
          </button>
        ) : (
          <SosStatusPill status={s.status} />
        )}
      </div>
      <div className="card-body">
        <div className="sos-grid">
          <div className="stack-sm">
            <span className="overline">Raised by</span>
            <div className="strong" style={{ fontSize: 19 }}>
              {raiser?.name ?? 'Unknown'}
            </div>
            {raiser?.phone && (
              <a className="call-btn" href={`tel:${raiser.phone}`}>
                <IconPhone /> {phone(raiser.phone)}
              </a>
            )}
            {s.note && <Note>“{s.note}”</Note>}
          </div>
          <div className="stack-sm">
            <span className="overline">{s.raisedByType === 'RIDER' ? 'Driver' : 'Rider'}</span>
            {other ? (
              <>
                <div className="strong" style={{ fontSize: 17 }}>
                  {other.name ?? 'Unknown'}
                </div>
                {snap?.driver?.vehicle && s.raisedByType === 'RIDER' && (
                  <div className="small muted">
                    {VEHICLE_LABEL[snap.driver.vehicle.type]} · {snap.driver.vehicle.plateDisplay} · {snap.driver.vehicle.colour}
                  </div>
                )}
                <a className="call-btn" href={`tel:${other.phone}`}>
                  <IconPhone /> {phone(other.phone)}
                </a>
              </>
            ) : (
              <p className="muted">No ride attached.</p>
            )}
            <div className="small muted stack-xs">
              {s.acknowledgedAt && (
                <span>
                  Taken by {staffName(s.acknowledgedBy)} at {time(s.acknowledgedAt)}
                </span>
              )}
              {s.resolvedAt && (
                <span>
                  Resolved by {staffName(s.resolvedBy)} at {time(s.resolvedAt)}
                  {s.resolutionNote ? ` · “${s.resolutionNote}”` : ''}
                </span>
              )}
            </div>
          </div>
          <div className="stack-sm">
            {where ? (
              <>
                <div className="mini-map">
                  <Suspense fallback={null}>
                    <RouteMap label="Where the SOS was raised" sos={where} pickup={snap?.pickup ?? null} drop={snap?.drop ?? null} />
                  </Suspense>
                </div>
                <a
                  className="link small row"
                  style={{ gap: 6 }}
                  target="_blank"
                  rel="noreferrer"
                  href={`https://www.google.com/maps/search/?api=1&query=${where.lat},${where.lng}`}
                >
                  <IconExternal /> Open in Google Maps · {where.lat.toFixed(4)}, {where.lng.toFixed(4)}
                </a>
              </>
            ) : (
              <p className="muted small">The phone didn’t send a location.</p>
            )}
          </div>
        </div>
        <div className="row" style={{ justifyContent: 'flex-end', marginTop: 18, gap: 8 }}>
          {s.rideId && (
            <Link to="/rides/$rideId" params={{ rideId: s.rideId }} className="btn">
              Open ride
            </Link>
          )}
          {s.status !== 'RESOLVED' && (
            <button className="btn" onClick={() => onResolve(s)}>
              Resolve
            </button>
          )}
        </div>
      </div>
    </section>
  );
}

type Filter = 'ACTIVE' | SosStatus;

export function SosPage() {
  const search = sosRoute.useSearch();
  const nav = sosRoute.useNavigate();
  const qc = useQueryClient();
  const sound = useSosSound();
  const [flash, show] = useFlash();
  const [resolving, setResolving] = useState<SosRecord | null>(null);
  const filter: Filter = search.status ?? 'ACTIVE';
  const page = search.page ?? 1;
  const q = useQuery({
    queryKey: [...qk.sos, 'list', filter, page],
    queryFn: async (): Promise<Page<SosRecord>> => {
      if (filter !== 'ACTIVE') return api.get<Page<SosRecord>>('/admin/sos', { status: filter, page, pageSize: 20 });
      const [open, ack] = await Promise.all([
        api.get<Page<SosRecord>>('/admin/sos', { status: 'OPEN', pageSize: 50 }),
        api.get<Page<SosRecord>>('/admin/sos', { status: 'ACKNOWLEDGED', pageSize: 50 }),
      ]);
      const items = [...open.items, ...ack.items];
      return { items, page: 1, pageSize: 100, total: items.length };
    },
    placeholderData: keepPreviousData,
    refetchInterval: 10_000,
  });

  return (
    <div className="stack">
      <PageHead
        title="SOS"
        sub="Alerts from riders and drivers. Take one, call them, then resolve it with a note."
        actions={
          <button
            className="btn"
            aria-pressed={sound}
            onClick={() => {
              setSosSound(!sound);
              if (!sound) playSosChime();
            }}
          >
            {sound ? <IconSound /> : <IconMute />} {sound ? 'Sound on' : 'Sound off'}
          </button>
        }
      />
      <Chips<Filter>
        label="Filter"
        value={filter}
        options={[
          { value: 'ACTIVE', label: 'Needs someone' },
          { value: 'RESOLVED', label: 'Resolved' },
        ]}
        onChange={(v) => nav({ search: { status: v === 'ACTIVE' ? undefined : v } })}
      />
      <Note>If someone is in danger, they should call 112. Zoomo Rides support follows up.</Note>
      <FlashNote flash={flash} />
      {q.isLoading ? (
        <section className="card">
          <Loading />
        </section>
      ) : q.error ? (
        <ErrorNote error={q.error} />
      ) : !q.data?.items.length ? (
        <section className="card">
          <Empty title={filter === 'ACTIVE' ? 'No open SOS' : 'Nothing here'} icon={IconSos}>
            {filter === 'ACTIVE' ? 'New alerts appear here, and as a red strip on every page, the moment they’re raised.' : undefined}
          </Empty>
        </section>
      ) : (
        <>
          {q.data.items.map((s) => (
            <SosCard key={s.id} s={s} onResolve={setResolving} onFlash={show} />
          ))}
          <Pager page={page} pageSize={q.data.pageSize} total={q.data.total} onPage={(p) => nav({ search: { ...search, page: p } })} />
        </>
      )}
      {resolving && (
        <ReasonDialog
          title="Resolve this SOS?"
          body="Write what happened, for the record."
          confirmLabel="Resolve"
          required={false}
          placeholder="For example: called the rider, all fine, wrong button"
          onClose={() => setResolving(null)}
          onConfirm={async (note) => {
            await api.post(`/admin/sos/${resolving.id}/resolve`, note ? { note } : {});
            show('SOS resolved.');
            void qc.invalidateQueries({ queryKey: qk.sos });
            void qc.invalidateQueries({ queryKey: qk.dashboard });
          }}
        />
      )}
    </div>
  );
}
